import { loadStripe } from '@stripe/stripe-js'
import type { Stripe, StripeConstructorOptions } from '@stripe/stripe-js'
import { captureError } from '@/utils/sentry'

/**
 * loadStripe() with retries (QA-149): a transient network hiccup on the first
 * script fetch showed the payment-form error straight away. @stripe/stripe-js
 * clears its cached script promise on failure, so calling loadStripe() again
 * genuinely re-injects the script. A rejection and a null resolution are both
 * treated as "not loaded yet"; after the last attempt the failure is reported
 * to Sentry once and the caller gets null — the same signal the call sites
 * already handle. This does not help a script blocked outright (adblock):
 * those fail every attempt and reach the same error message, just a few
 * seconds later.
 */
export async function loadStripeWithRetry(
  publishableKey: string,
  options?: StripeConstructorOptions,
  attempts = 3,
  baseDelayMs = 1200
): Promise<Stripe | null> {
  for (let attempt = 1; attempt <= attempts; attempt++) {
    try {
      const stripe = await loadStripe(publishableKey, options)
      if (stripe) return stripe
    } catch {
      // fall through to the delay + next attempt
    }

    if (attempt < attempts) {
      await new Promise((resolve) => setTimeout(resolve, baseDelayMs * attempt))
    }
  }

  // Reported here so every payment page sends the same single event.
  captureError(new Error('Failed to load Stripe.js'), { tags: { integration: 'stripe' } })
  return null
}
